// Общото между списъка „Излишък от продажби" и разбивката му по отчети:
// типът на реда, сглобяването на редовете от `GET /inventory/shortages`,
// групите по склад на списъка с отметки и router state-ът към листа „Нова
// доставка" (компенсирането).
//
// Сметката е непроменена спрямо предишната версия на страницата: цената
// (последна доставна, иначе среднопретеглена) е ВИНАГИ нето → „Нето" =
// излишък × цена, а „Бруто" се получава по ставката на продукта САМО когато
// фирмата е регистрирана по ДДС (иначе бруто = нето).
import type {
  Product,
  Shortage,
  ShortageBreakdown,
} from "../../../../api/inventory";
import type { DeliveryRouterState } from "../../delivery/components/useDeliveryLoad";
import { clampDecimals } from "../../components/qty";

// Парите в модула са с ДЕСЕТИЧНА ТОЧКА и интервал за хилядите: „1 234.56" —
// едно копие за целия склад (`components/numbers`).
export { formatMoney, formatUnitPrice } from "../../components/numbers";

// „20.07.2026" — ръчен формат, защото bg-BG locale добавя „ г." (както при
// `fmtDateTime` за списъчните таблици).
export const formatDate = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

// Един ред от списъка: непокрит излишък от продажби на (склад, продукт).
export type ShortageRow = {
  // един продукт може да е в недостиг в няколко склада
  key: string; // `${warehouseId}:${productId}`
  productId: number;
  productName: string;
  decimals: number; // знаци за мерната единица (decimal_places)
  unit: string | null;
  category: string | null;
  warehouseId: number;
  warehouseName: string;
  quantity: number; // непокритият излишък (положителен; минусът е само визуален)
  lastDeliveryPrice: number | null;
  // нето → бруто за този продукт: 1 + ставка/100 при регистрирана по ДДС фирма,
  // иначе 1 (нерегистрираната няма насрещно ДДС → бруто = нето)
  grossFactor: number;
  quantityNet: number; // излишък × цена (нето)
  quantityGross: number; // нето × grossFactor
  earliestReportDate: string | null; // най-старият отчет-причинител (задна дата)
  breakdown: ShortageBreakdown[]; // разбивка по отчети
};

export function buildShortageRows(
  shortages: Shortage[],
  productById: Map<number, Product>,
  vatRegistered: boolean,
): ShortageRow[] {
  return shortages.map((shortage) => {
    const product = productById.get(shortage.productId);
    const price = shortage.lastDeliveryPrice ?? shortage.weightedAvgPrice ?? 0;
    const vatRate = product?.vatRate ?? 0;
    const grossFactor = vatRegistered && vatRate ? 1 + vatRate / 100 : 1;
    const net = shortage.deficit * price;
    return {
      key: `${shortage.warehouseId}:${shortage.productId}`,
      productId: shortage.productId,
      productName:
        shortage.productName || product?.name || `#${shortage.productId}`,
      decimals: clampDecimals(product?.decimalPlaces),
      unit: shortage.unit ?? product?.unit ?? null,
      category: product?.category ?? null,
      warehouseId: shortage.warehouseId,
      warehouseName: shortage.warehouseName || "—",
      quantity: shortage.deficit,
      lastDeliveryPrice: shortage.lastDeliveryPrice ?? null,
      grossFactor,
      quantityNet: net,
      quantityGross: net * grossFactor,
      earliestReportDate: shortage.earliestDate ?? null,
      breakdown: shortage.breakdown ?? [],
    };
  });
}

// Търсене по продукт, склад и категория (всяка дума трябва да се среща).
export function filterShortageRows(
  rows: ShortageRow[],
  search: string,
): ShortageRow[] {
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return rows;
  return rows.filter((row) => {
    const haystack = [row.productName, row.category ?? "", row.warehouseName]
      .join(" ")
      .toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

// Категорията като стойност на филтъра — продукт без категория е „—"
export const categoryOf = (row: ShortageRow) => row.category ?? "—";

// ─── Групи по склад (списъкът с отметки) ───────────────────────────────────
export type ShortageSort = "name" | "value";

export type ShortageWarehouseGroup = {
  warehouseId: number;
  warehouseName: string;
  rows: ShortageRow[];
  net: number;
  gross: number;
};

// Складовете по азбучен ред; вътре — по име на продукта или по стойност
// (бруто, най-скъпият недостиг най-горе).
export function groupShortageRows(
  rows: ShortageRow[],
  sort: ShortageSort,
): ShortageWarehouseGroup[] {
  const byWarehouse = new Map<number, ShortageWarehouseGroup>();
  for (const row of rows) {
    const group = byWarehouse.get(row.warehouseId) ?? {
      warehouseId: row.warehouseId,
      warehouseName: row.warehouseName,
      rows: [],
      net: 0,
      gross: 0,
    };
    group.rows.push(row);
    group.net += row.quantityNet;
    group.gross += row.quantityGross;
    byWarehouse.set(row.warehouseId, group);
  }
  const byName = (a: ShortageRow, b: ShortageRow) =>
    a.productName.localeCompare(b.productName, "bg");
  const compareRows =
    sort === "value"
      ? (a: ShortageRow, b: ShortageRow) =>
          b.quantityGross - a.quantityGross || byName(a, b)
      : byName;
  return [...byWarehouse.values()]
    .map((group) => ({ ...group, rows: [...group.rows].sort(compareRows) }))
    .sort((a, b) => a.warehouseName.localeCompare(b.warehouseName, "bg"));
}

// ─── Компенсиране с доставка ───────────────────────────────────────────────
// Router state към `/inventory/delivery/new` — договорът е `DeliveryRouterState`
// (листът го чете в `useDeliveryLoad`), НЕ го разширявай оттук. Всеки продукт
// носи СВОЯ склад, количество и последна цена; датата по подразбиране е
// най-старият отчет-причинител сред избраните (в листа може да се смени).
export function compensationStateOf(
  rows: ShortageRow[],
): NonNullable<DeliveryRouterState> {
  const prefill = rows.map((row) => ({
    productId: row.productId,
    quantity: row.quantity,
    unitPrice: row.lastDeliveryPrice,
    warehouseId: row.warehouseId,
  }));
  return { prefill, prefillDate: oldestReportDateOf(rows) };
}

// Най-старият отчет-причинител сред редовете (ISO низовете се сравняват
// лексикографски) или null, ако никой няма дата.
export function oldestReportDateOf(rows: ShortageRow[]): string | null {
  const dates = rows
    .map((row) => row.earliestReportDate)
    .filter((date): date is string => !!date);
  return dates.length
    ? dates.reduce((oldest, date) => (date < oldest ? date : oldest))
    : null;
}
